
import ErrorMessage from '@/components/UI/ErrorMessage.vue'
import { ActionType, EventType, PageName } from '@/types/track-events'
import countries from '@/util/countries.ts'
import states from '@/util/states.ts'
import { trackCustomEvent, trackCustomFieldEvent } from '@/util/trackEvent'
import arrowRight from '@iconify/icons-mdi/arrow-right'
import { Icon, addIcon } from '@iconify/vue'
import { defineComponent, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { logOnboardingGaEvent } from '../util/gaEvent'
import Tooltip from './Tooltip.vue'

addIcon('arrow-right', arrowRight)

export default defineComponent({
  components: {
    Icon,
    ErrorMessage,
    Tooltip
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const addressLine = ref(store.state.business.addressLine)
    const address = ref(store.state.business.address)
    const country = ref(store.state.business.country)
    const state = ref(store.state.business.region)
    const city = ref(store.state.business.city)
    const zipcode = ref(store.state.business.zipcode)
    const isReselling = ref(store.state.business.isReselling)
    const termsnCondition = ref(store.state.business.tandc)

    const formErrors = ref({
      addressError: false,
      countryError: false,
      stateError: false,
      cityError: false,
      zipcodeError: false,
      isResellingError: false,
      termsnConditionError: false
    })

    const sendCustomFieldEvent = (val: string, fieldName: string) => {
      trackCustomFieldEvent({
        pageName: PageName.ONBOARDING_BILLING_ADDRESS,
        elementName: fieldName,
        value: val,
        store
      })
    }

    function sendCustomEvent(
      eventName: string,
      eventType?: EventType,
      actionType?: ActionType
    ) {
      trackCustomEvent({
        eventType: eventType ? eventType : EventType.PAGE_ACTION,
        actionType: actionType ? actionType : ActionType.CLICK,
        pageName: PageName.ONBOARDING_BILLING_ADDRESS,
        elementName: eventName,
        store
      })
    }

    watch(state, () => {
      formErrors.value.stateError = false
    })

    watch(termsnCondition, () => {
      sendCustomFieldEvent(
        termsnCondition.value ? 'true' : 'false',
        'onboarding_terms_condition'
      )
    })

    const verifyErrors = () => {
      if (!country.value) formErrors.value.countryError = true
      if (!state.value || !state.value?.trim())
        formErrors.value.stateError = true
      if (!address.value || !address.value?.trim()) {
        address.value = address.value?.trim()
        formErrors.value.addressError = true
      }
      if (!city.value || !city.value?.trim()) {
        city.value = city.value?.trim()
        formErrors.value.cityError = true
      }
      if (!zipcode.value || !zipcode.value?.trim()) {
        zipcode.value = zipcode.value?.trim()
        formErrors.value.zipcodeError = true
      }
      if (typeof isReselling.value === 'undefined') {
        formErrors.value.isResellingError = true
      }
      if (termsnCondition.value !== true) {
        formErrors.value.termsnConditionError = true
      }
    }

    const updateCountry = (value: string) => {
      formErrors.value.countryError = false
      country.value = value
      state.value = ''
      sendCustomFieldEvent(value, 'onboarding_address_country')
    }

    const updateState = (value: string) => {
      formErrors.value.stateError = false
      state.value = value
      sendCustomFieldEvent(value, 'onboarding_address_state')
    }

    const prevStep = () => {
      sendCustomEvent('back', EventType.PAGE_ACTION, ActionType.CLICK)
      router.push({
        name: 'businessInfoPage'
      })
    }

    const updateIsReseeling = () => {
      formErrors.value.isResellingError = false
      sendCustomFieldEvent(
        isReselling.value ? 'yes' : 'no',
        'onboarding_reselling'
      )
    }

    const nextStep = () => {
      verifyErrors()
      for (const error in formErrors.value) {
        if (formErrors.value[error]) return
      }
      store.commit('business/updateAddress', {
        address: address.value.trim(),
        addressLine: addressLine.value.trim(),
        country: country.value,
        region: state.value.trim(),
        city: city.value.trim(),
        zipcode: zipcode.value.trim(),
        isReselling: isReselling.value,
        termsnCondition: termsnCondition.value
      })

      // @ts-ignore
      store?.state?.iframe?.handshake?.then(parent => {
        parent.emit('ga-event', 'onb-biz-address')

        const gaData = {
          event: 'onboarding_address_complete',
          onboarding_step: '2'
        }
        logOnboardingGaEvent(gaData, store)
      })

      trackCustomEvent({
        eventType: EventType.PAGE_ACTION,
        actionType: ActionType.CLICK,
        pageName: PageName.ONBOARDING_BILLING_ADDRESS,
        elementName: PageName.ONBOARDING_BILLING_ADDRESS,
        store
      })

      router.push({
        name: 'businessToolsPage'
      })
    }

    const clearState = () => {
      formErrors.value.countryError = false
      state.value = ''
      if (country.value) {
        sendCustomFieldEvent(country.value, 'onboarding_address_country')
      }
    }

    onMounted(() => {
      sendCustomEvent(
        PageName.ONBOARDING_BILLING_ADDRESS,
        EventType.PAGE_VISIT,
        ActionType.VIEW
      )
    })

    return {
      address,
      addressLine,
      country,
      state,
      city,
      zipcode,
      nextStep,
      prevStep,
      formErrors,
      updateCountry,
      updateState,
      states,
      countries,
      clearState,
      isReselling,
      updateIsReseeling,
      termsnCondition,
      sendCustomFieldEvent,
      sendCustomEvent
    }
  }
})
